import { yupResolver } from '@hookform/resolvers/yup'
import { FC, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import s from './AddStreamPopup.module.scss'

import { METAVERSE_IMAGE_BY_NAME } from 'constants/MetaverseIcons'

import { useData } from 'contexts/CreateCreativeFormContext'

import { ExclamationIcon } from 'assets/icons/ExclamationIcon'
import { GlobeIcon } from 'assets/icons/GlobeIcon'
import { InfoIcon } from 'assets/icons/InfoIcon'
import { LinkIcon } from 'assets/icons/LinkIcon'
import { MetaAdsIcon } from 'assets/icons/MetaAdsIcon'
import { PlayIcon } from 'assets/icons/PlayIcon'
import { TwitchIcon } from 'assets/icons/socials/TwitchIcon'
import { YoutubeIcon } from 'assets/icons/socials/YoutubeIcon'

import { CustomDropzone } from 'components/Controls/CustomDropzone/CustomDropzone'
import { InputControlled } from 'components/Controls/Input/InputControlled'
import { SelectControlled } from 'components/Controls/Select/SelectControlled'
import { CustomButton } from 'components/UI/CustomButton/CustomButton'
import { ModalFullScreen } from 'components/UI/ModalFullScreen/ModalFullScreen'

import { makeStreamStep1Schema } from 'pages/MyCreatives/validation'

interface Step1Props {
  setActivePage: () => void
  onHide: () => void
}

interface FormType {
  files: File[]
  streamUrl: string
  meta: { value: string; label: string }
}

const METAVERSE_OPTIONS = [
  { value: 'Decentraland', label: 'Decentraland' },
  { value: 'Voxels', label: 'Voxels' },
]

export const Step1: FC<Step1Props> = (props) => {
  const { setActivePage, onHide } = props
  const [substep, setSubstep] = useState(1)
  const [streamType, setStreamType] = useState<'M3U' | 'Twitch' | 'YouTube' | 'metaads'>('M3U')
  const { setValues } = useData()

  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
  } = useForm<FormType>({
    mode: 'onChange',
    resolver: yupResolver(makeStreamStep1Schema(streamType)),
    defaultValues: {
      files: [],
      streamUrl: '',
      meta: { value: '', label: 'Select Metaverse...' },
    },
  })
  const selectedMeta = watch('meta')
  const onSubmit = async (fData: FormType) => {
    setValues({
      ...fData,
      creativeType: streamType === 'metaads' ? 3 : streamType === 'Twitch' ? 5 : streamType === 'YouTube' ? 4 : 2,
      streamUrl: streamType === 'metaads' ? null : fData.streamUrl,
    })
    setActivePage()
  }

  const handleFirstStep = () => {
    setSubstep(2)
  }

  useEffect(() => {
    if (selectedMeta.value === 'Voxels') setStreamType('YouTube')
    if (selectedMeta.value === 'Decentraland') setStreamType('M3U')
  }, [selectedMeta.value])

  return (
    <ModalFullScreen onHide={onHide} className={s.wrapper}>
      {substep === 1 ? (
        <div className={s.firstStepWrapper}>
          <h1 className={s.header}>Add Stream</h1>
          <SelectControlled
            control={control}
            name="meta"
            label="Select a desired Metaverse:"
            options={METAVERSE_OPTIONS}
            optionsIcons={METAVERSE_IMAGE_BY_NAME}
            placeholder="Decentraland"
            icon={<GlobeIcon />}
            className={s.selectMetaverse}
          />

          <div className={s.buttonsWrapper}>
            <CustomButton variant="primary" onClick={handleFirstStep} size="240" disabled={selectedMeta.value === ''}>
              Next
            </CustomButton>
            <CustomButton variant="outlined" size="152" onClick={onHide}>
              Close
            </CustomButton>
          </div>
        </div>
      ) : (
        <>
          <div className={s.topWrapper}>
            <h1 className={s.header}>Add Stream</h1>
            <div className={s.streamSelectWrapper}>
              {selectedMeta.value === 'Decentraland' ? (
                <>
                  <button type="button" className={s.streamSelect} onClick={() => setStreamType('M3U')}>
                    <LinkIcon className={streamType === 'M3U' ? s.selected : s.default} />
                  </button>
                  <button type="button" className={s.streamSelect} onClick={() => setStreamType('metaads')}>
                    <MetaAdsIcon className={streamType === 'metaads' ? s.selected : s.default} />
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className={s.streamSelect} onClick={() => setStreamType('YouTube')}>
                    <YoutubeIcon className={streamType === 'YouTube' ? s.selected : s.default} />
                  </button>
                  <button type="button" className={s.streamSelect} onClick={() => setStreamType('Twitch')}>
                    <TwitchIcon className={streamType === 'Twitch' ? s.selected : s.default} />
                  </button>
                </>
              )}
            </div>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className={s.formWrapper} id="CreateStreamStep1">
            <div className={s.controlsWrapper}>
              <CustomDropzone
                control={control}
                name="files"
                noMediaTitle="Drag & Drop a Preview Image"
                // @ts-ignore
                error={errors?.files?.message}
                wrapperStyles={{ alignItems: 'center', alignSelf: 'center', maxWidth: 400, width: '100%' }}
                dropzoneStyles={{
                  maxWidth: 400,
                  width: '100%',
                  height: 225,
                  borderRadius: 8,
                  margin: '0 auto',
                }}
                noMediaFont={18}
                iconWidth={48}
                iconHeight={48}
                alignItems="center"
              />
              {streamType === 'metaads' ? (
                <a href="https://obsproject.com/" className={s.link} target="_blank" rel="noreferrer">
                  <InfoIcon className={s.linkIcon} /> How to use it?
                </a>
              ) : null}
              {streamType === 'metaads' ? null : (
                <>
                  <InputControlled
                    control={control}
                    name="streamUrl"
                    icon={<PlayIcon />}
                    placeholder={`${streamType} Stream URL...`}
                    error={errors?.streamUrl?.message}
                  />
                  <div className={s.exclamation}>
                    <ExclamationIcon className={s.exclamationIcon} />
                    <p className={s.exclamationText}>
                      Be advised. All the content you upload will be sent to moderation which takes up to 48 hours. All
                      inappropriate content will lead to ban.
                    </p>
                  </div>
                </>
              )}
            </div>
          </form>
          <div
            className={s.buttonsWrapper}
            style={{ justifyContent: streamType === 'metaads' ? 'space-between' : 'center' }}
          >
            <CustomButton variant="primary" type="submit" size="240" form="CreateStreamStep1" className={s.nextBtn}>
              Next
            </CustomButton>
            <CustomButton variant="outlined" size="152" onClick={onHide}>
              Close
            </CustomButton>
          </div>
        </>
      )}
    </ModalFullScreen>
  )
}

export default Step1
